import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

import { streamingSpecsService } from '@/services/providers';
import { planQueries } from '@/services/plans/queries';
import { toast } from 'sonner';

interface StreamingFeaturesManagementProps {
  providerId: number;
  versionId: number;
  regionId?: number;
}

interface StreamingSpecData {
  max_video_quality?: string | null;
  hdr_support?: boolean | null;
  dolby_atmos_support?: boolean | null;
  simultaneous_streams?: number | null;
  offline_downloads?: boolean | null;
  download_limit_days?: number | null;
}

interface Plan {
  id: number;
  name: string;
  channel_count?: number;
}

export function StreamingFeaturesManagement({ 
  providerId, 
  versionId, 
  regionId 
}: StreamingFeaturesManagementProps) {
  const [appliesTo, setAppliesTo] = useState<'provider' | 'plan' | 'category' | 'package'>('provider');
  const [selectedPlans, setSelectedPlans] = useState<number[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [providerData, setProviderData] = useState<StreamingSpecData>({});
  const [planData, setPlanData] = useState<StreamingSpecData>({});
  const [loading, setLoading] = useState(false);
  const [providerTemplateSaved, setProviderTemplateSaved] = useState(false);
  const [planOverridesSaved, setPlanOverridesSaved] = useState<Record<number, boolean>>({});

  // Load existing data
  useEffect(() => {
    loadData();
  }, [providerId, versionId, regionId]);

  // Auto-switch to plan mode when plan overrides exist (after refresh)
  useEffect(() => {
    if (Object.keys(planOverridesSaved).length > 0 && appliesTo === 'provider') {
      setAppliesTo('plan');
    }
  }, [planOverridesSaved, appliesTo]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load existing provider template
      const template = await streamingSpecsService.getProviderTemplate(providerId, versionId, regionId);
      if (template) {
        setProviderData(template);
        setProviderTemplateSaved(true);
        // Initialize plan data with provider data
        setPlanData({ ...template });
      }

      // Load plans
      const plansData = await planQueries.getPlans(providerId, versionId);
      setPlans(plansData || []);

      // Load existing plan overrides from override tables if any
      if (plansData && plansData.length > 0) {
        const planIds = plansData.map((plan: any) => plan.id);
        const planOverrides = await streamingSpecsService.getPlanOverrides(planIds);
        
        if (planOverrides && planOverrides.length > 0) {
          // Mark plans with overrides as saved
          const savedPlans = planOverrides.map((override: any) => override.plan_id) as number[];
          
          const savedState: Record<number, boolean> = {};
          savedPlans.forEach((planId: number) => {
            savedState[planId] = true;
          });
          setPlanOverridesSaved(savedState);
          
          // If there are plan overrides, select those plans but keep current mode
          if (savedPlans.length > 0) {
            setSelectedPlans(savedPlans);
            
            // Load the first plan's override data to show in the form
            const firstPlanOverride = planOverrides[0];
            if (firstPlanOverride) {
              setPlanData({
                max_video_quality: firstPlanOverride.max_video_quality,
                hdr_support: firstPlanOverride.hdr_support,
                dolby_atmos_support: firstPlanOverride.dolby_atmos_support,
                simultaneous_streams: firstPlanOverride.simultaneous_streams,
                offline_downloads: firstPlanOverride.offline_downloads,
                download_limit_days: firstPlanOverride.download_limit_days,
              });
            }
          }
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error("Failed to load existing data", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProviderTemplateSave = async () => {
    setLoading(true);
    try {
      // Use upsert to handle both create and update
      const result = await streamingSpecsService.upsertProviderTemplate({
        provider_id: providerId,
        version_id: versionId,
        region_id: regionId,
        ...providerData
      });
      
      console.log('Provider template saved successfully:', result);
      setProviderTemplateSaved(true);
      
      // Copy provider data to plan data
      setPlanData({ ...providerData });

      toast.success("Provider template saved successfully", {
        style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
      });

      // Reload data to verify the save worked
      await loadData();
    } catch (error) {
      console.error('Error saving provider template:', error);
      
      // Handle specific error cases
      if (error instanceof Error) {
        if (error.message.includes('column') && error.message.includes('does not exist')) {
          toast.error("Database constraint error. Please contact support.", {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        } else if (error.message.includes('duplicate key value violates unique constraint')) {
          toast.success("Streaming features updated successfully (existing record was modified).", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else if (error.message.includes('23505')) {
          toast.success("Streaming features updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else {
          toast.error(error.message, {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        }
      } else {
        toast.error("Failed to save provider template", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlanTemplateSave = async () => {
    if (selectedPlans.length === 0) {
      toast.error("Please select at least one plan", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
      return;
    }

    setLoading(true);
    try {
      // Save overrides for all selected plans
      const planNames = selectedPlans.map(id => plans.find(p => p.id === id)?.name).filter(Boolean);
      
      for (const planId of selectedPlans) {
        try {
          const result = await streamingSpecsService.upsertPlanOverride(planId, planData);
          console.log(`Plan override saved for plan ${planId}:`, result);
          
          // Mark this plan as saved
          setPlanOverridesSaved(prev => ({ ...prev, [planId]: true }));
        } catch (planError) {
          console.error(`Error saving override for plan ${planId}:`, planError);
          throw new Error(`Failed to save override for plan ${planId}: ${planError instanceof Error ? planError.message : 'Unknown error'}`);
        }
      }

      toast.success(`Plan overrides saved for: ${planNames.join(', ')}`, {
        style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
      });

      // Reload data to verify the save worked
      await loadData();
    } catch (error) {
      console.error('Error saving plan template:', error);
      
      // Enhanced error handling with different colors
      if (error instanceof Error) {
        if (error.message.includes('duplicate key value violates unique constraint')) {
          toast.success("Plan streaming features updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else if (error.message.includes('23505')) {
          toast.success("Plan streaming features updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else {
          toast.error(error.message, {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        }
      } else {
        toast.error("Failed to save plan template", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetPlanToProviderTemplate = () => {
    setPlanData({ ...providerData });
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Streaming Features Management</h2>
          <p className="text-muted-foreground">Configure streaming specifications and features for this provider.</p>
        </div>
        <Badge variant="outline">
          {appliesTo === 'provider' && 'Provider Template'}
          {appliesTo === 'plan' && 'Plan Overrides'}
          {appliesTo === 'category' && 'Category Overrides'}
          {appliesTo === 'package' && 'Package Overrides'}
        </Badge>
      </div>

      {/* Applies To Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Applies To</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={appliesTo}
            onValueChange={(value: 'provider' | 'plan' | 'category' | 'package') => setAppliesTo(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="provider">Provider-wide</SelectItem>
              <SelectItem value="plan">Plan-wise</SelectItem>
              <SelectItem value="category">Category-wise</SelectItem>
              <SelectItem value="package">Package-wise</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Provider Template Component (Always shown) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Template Configuration</CardTitle>
              <p className="text-sm text-muted-foreground">
                These values will apply to all plans by default.
              </p>
            </div>
            {providerTemplateSaved && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                ✓ Saved
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <StreamingSpecForm
            data={providerData}
            onChange={setProviderData}
            disabled={false}
          />
          <div className="flex justify-end mt-4">
            <Button 
              onClick={handleProviderTemplateSave}
              disabled={loading}
              variant={providerTemplateSaved ? "outline" : "default"}
            >
              {loading ? 'Saving...' : providerTemplateSaved ? 'Update Template' : 'Save Template'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Plan Template Component (Shown when plan-wise is selected OR when plan overrides exist) */}
      {(appliesTo === 'plan' || Object.keys(planOverridesSaved).length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Plan Template Configuration</CardTitle>
              <div className="flex gap-2">
                {selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    ✓ Overrides Saved
                  </Badge>
                )}
                {providerTemplateSaved && !selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary">Editable</Badge>
                )}
                {!providerTemplateSaved && (
                  <Badge variant="outline">Read-only until provider template saved</Badge>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {providerTemplateSaved 
                ? 'Edit these values to override the provider template for selected plans.'
                : 'Save the provider template first to enable plan-specific overrides.'
              }
            </p>
            
            {/* Plan Selection Warning */}
            {selectedPlans.length === 0 && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-yellow-800 font-medium">
                    ⚠️ Please select at least one plan to configure plan-specific streaming features
                  </span>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Plan Selection */}
            <div className="mb-6 space-y-2">
              <Label>Select Plans for Override</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {plans.length === 0 ? (
                  <p className="text-sm text-muted-foreground col-span-2">No plans available</p>
                ) : (
                  plans.map((plan) => (
                    <div key={plan.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`plan-${plan.id}`}
                        checked={selectedPlans.includes(plan.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPlans([...selectedPlans, plan.id]);
                          } else {
                            setSelectedPlans(selectedPlans.filter(id => id !== plan.id));
                          }
                        }}
                      />
                      <Label htmlFor={`plan-${plan.id}`} className="text-sm flex items-center gap-2">
                        {plan.name}
                        {planOverridesSaved[plan.id] && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                            ✓
                          </Badge>
                        )}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Plan Template Form */}
            <StreamingSpecForm
              data={planData}
              onChange={setPlanData}
              disabled={!providerTemplateSaved}
            />
            
            {providerTemplateSaved && (
              <div className="flex justify-between mt-4">
                <Button 
                  variant="outline"
                  onClick={resetPlanToProviderTemplate}
                >
                  Reset to Provider Template
                </Button>
                <Button 
                  onClick={handlePlanTemplateSave}
                  disabled={loading || selectedPlans.length === 0}
                  variant={selectedPlans.some(planId => planOverridesSaved[planId]) ? "outline" : "default"}
                  className="transition-all duration-200 ease-in-out hover:scale-105"
                >
                  {loading ? 'Saving...' : selectedPlans.some(planId => planOverridesSaved[planId]) ? 'Update Plan Overrides' : 'Save Plan Template'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface StreamingSpecFormProps {
  data: StreamingSpecData;
  onChange: (data: StreamingSpecData) => void;
  disabled?: boolean;
}

function StreamingSpecForm({ data, onChange, disabled }: StreamingSpecFormProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="max_video_quality">Max Video Quality</Label>
          <Select
            value={data.max_video_quality || ''}
            onValueChange={(value) => onChange({ ...data, max_video_quality: value })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select quality" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="HD (1080p)">HD (1080p)</SelectItem>
              <SelectItem value="4K UHD">4K UHD</SelectItem>
              <SelectItem value="8K">8K</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="simultaneous_streams">Simultaneous Streams</Label>
          <Input
            type="number"
            value={data.simultaneous_streams || ''}
            onChange={(e) => onChange({ 
              ...data, 
              simultaneous_streams: e.target.value ? parseInt(e.target.value) : null 
            })}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="download_limit_days">Download Limit (days)</Label>
          <Input
            type="number"
            value={data.download_limit_days || ''}
            onChange={(e) => onChange({ 
              ...data, 
              download_limit_days: e.target.value ? parseInt(e.target.value) : null 
            })}
            disabled={disabled}
          />
        </div>

        <div className="flex items-center space-x-2 pt-8">
          <Switch
            checked={data.offline_downloads || false}
            onCheckedChange={(checked) => onChange({ ...data, offline_downloads: checked })}
            disabled={disabled}
          />
          <Label>Offline Downloads</Label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center space-x-2">
          <Switch
            checked={data.hdr_support || false}
            onCheckedChange={(checked) => onChange({ ...data, hdr_support: checked })}
            disabled={disabled}
          />
          <Label>HDR Support</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            checked={data.dolby_atmos_support || false}
            onCheckedChange={(checked) => onChange({ ...data, dolby_atmos_support: checked })}
            disabled={disabled}
          />
          <Label>Dolby Atmos</Label>
        </div>
      </div>
    </div>
  );
}